<script lang="ts">
import type { AcceptableValue } from '@nuxt/ui';
import type { SelectFieldOption, SelectFieldOptions } from '#layers/data-model';
import type { SelectMenuEmits, SelectMenuProps } from '#layers/ui';
import type { InputBlockProps } from '~/types/input-block';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';

interface InputSelectBlockProps extends
	SelectFieldOptions,
	InputBlockProps,
	Pick<SelectMenuProps, 'size' | 'variant' | 'color' | 'icon' | 'createItem' | 'placeholder' | 'ui' | 'trailingIcon' | 'open' | 'class' | 'modelModifiers'> {
	/**
	 * Convert the value to title case when no explicit label is provided
	 *
	 * @default true
	 */
	autoTitleCaseValue?: boolean;
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_select.name',
	description: '$t:blocks.input_select.description',
	icon: 'lucide:list-chevrons-up-down',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ['select'] },
	events: [
		{
			name: 'clear',
			payload: [],
		},
		{
			name: 'update:open',
			payload: [
				{
					name: 'value',
					type: 'boolean',
					required: true,
					array: false,
				},
			],
		},
	],

	options: ({ form, values }) => form([
		{
			name: 'modelValue',
			model: true,
		},
		selectOptionsFormSchema({ options: values.options }),
		{
			name: 'multiple',
		},
		{
			name: 'autoTitleCaseValue',
		},
		{
			name: 'placeholder',
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'icon',
			block: BuiltinBlockIds.InputIcon,
		},
		{
			name: 'disabled',
		},
		{
			name: 'disabledNotice',
		},
		{
			name: 'modelModifiers',
			hidden: true,
		},
		{
			name: 'readonly',
		},
	]),
}</script>

<script setup lang="ts">
import { createReusableTemplate, reactivePick } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { useAppConfig } from '#app/config';
import { ensureArray } from '#layers/base/utils/array';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { selectOptionsFormSchema } from '#layers/data-model';
import { useTranslationUtils } from '#layers/i18n';
import { mergeUi, useDisabledNotice, useFormField, WithScope } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';
import { useSelectOptions } from '~/composables/use-select-options';

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(defineProps<InputSelectBlockProps>(), {
	autoTitleCaseValue: true,
	placeholder: '$t:common.select_value',
});
const emits = defineEmits<SelectMenuEmits<any, any, any>>();
const model = defineModel<any>();

;

const appConfig = useAppConfig();
const forwarded = useForwardPropsEmits(reactivePick(props, 'size', 'variant', 'color', 'icon', 'ui', 'open'), emits);
const { modelValue, disabled, disabledNotice, nullable } = usePrimitiveFormField(props, { model });
const { normalizedOptions: items } = useSelectOptions(() => props.options);
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const { translateString } = useTranslationUtils();
const placeholder = computed(() => props.placeholder ? translateString(props.placeholder) : undefined);
const hasValue = computed(() => {
	if (props.multiple) return Array.isArray(modelValue.value) && modelValue.value.length > 0;
	return modelValue.value !== undefined && modelValue.value !== null && modelValue.value !== '';
});
const trailingIcon = computed(() => {
	if (props.trailingIcon !== undefined) return props.trailingIcon;
	if (props.readonly) return '';
	return props.multiple ? 'lucide:plus' : undefined;
});
const { emitFormChange, emitFormInput } = useFormField<InputSelectBlockProps>(props, { forwardContext: true });

function findOptions(values: Array<AcceptableValue> | AcceptableValue) {
	return ensureArray(values).map((value) => items.value.find((option) => option.value === value)).filter(Boolean) as SelectFieldOption[];
}

function getDisplayValue(option: SelectFieldOption) {
	return option.label ?? option.value;
}

function removeItem(value: AcceptableValue) {
	if (!modelValue.value) return;

	modelValue.value = (modelValue.value as Array<AcceptableValue>).filter((item) => item !== value) as Array<AcceptableValue>;
	emitFormChange();
	emitFormInput();
}

const [DefineItemIcon, ReuseItemIcon] = createReusableTemplate<{ item: SelectFieldOption; iconClass?: string }>({ inheritAttrs: false });
const [DefineValueItem, ReuseValueItem] = createReusableTemplate<{ item: SelectFieldOption; iconClass?: string }>({ inheritAttrs: false });
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<!-- TODO: <div> necessary because DTooltip doesn't play nice with SelectMenu -->
		<div>
			<DefineItemIcon v-slot="{ item, iconClass }">
				<span v-if="item.icon || item.color" class="inline-flex items-center justify-center" :class="iconClass">
					<DIcon v-if="item.icon" :name="item.icon" :style="{ color: item.color }" />
					<DColorDot v-else-if="item.color && !item.icon" :color="item.color" size="xs" />
				</span>
			</DefineItemIcon>

			<DefineValueItem v-slot="{ item, iconClass }">
				<span class="pointer-events-none inline-flex min-w-0 items-center gap-1.5">
					<ReuseItemIcon :item :icon-class />
					<span class="min-w-0 truncate">{{ getDisplayValue(item) }}</span>
				</span>
			</DefineValueItem>

			<DSelectMenu
				v-bind="{ ...forwarded, ...$attrs }"
				v-model="modelValue"
				:model-modifiers="modelModifiers ?? {}"
				value-key="value"
				:search-input="items.length > 5"
				:disabled
				:readonly
				:aria-readonly="readonly"
				:items
				:multiple
				:placeholder
				:clear="nullable"
				:ui="mergeUi(ui, {
					base: 'pointer-events-auto w-full flex-wrap gap-1',
				})"
				:trailing-icon
			>
				<template #default="{ modelValue: selectedValue }">
					<WithScope v-if="hasValue" v-slot="{ options }" :define="{ options: findOptions(modelValue) }">
						<slot v-bind="{ modelValue, multiple, options }">
							<template v-if="multiple && Array.isArray(modelValue)">
								<DNotFieldGroup>
									<DBadge
										v-for="option in options"
										:key="option.value as PropertyKey"
										variant="outline"
										color="neutral"
										rounded
									>
										<ReuseValueItem :item="option" />
										<template #trailing>
											<DButton
												variant="link"
												color="neutral"
												size="xs"
												class="-m-1 -mr-2"
												tabindex="-1"
												:icon="!readonly ? appConfig.ui.icons.remove : undefined"
												@click.stop.prevent="removeItem(option.value)"
											/>
										</template>
									</DBadge>
								</DNotFieldGroup>
							</template>
							<template v-else-if="options.length > 0">
								<ReuseValueItem v-for="option in options" :key="option.value as PropertyKey" :item="option" />
							</template>
							<span v-else>{{ selectedValue }}</span>
						</slot>
					</WithScope>
					<span v-else data-slot="placeholder" class="truncate text-dimmed">{{ placeholder }}</span>
				</template>

				<template v-if="!readonly" #item-leading="{ item, ui }">
					<ReuseItemIcon :item :icon-class="ui?.itemLeadingIcon()" />
				</template>
			</DSelectMenu>
		</div>
	</DTooltip>
</template>
